import React, { useCallback, useState } from "react";
import { Navigate, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useBrand } from "../context/BrandContext";
import { useAuthContext } from "../context/AuthContext";
import { loginApi, setBearerToken } from "../api/client";
import LegacyPasswordUpgradeDialog from "../components/LegacyPasswordUpgradeDialog";
import { setUiLocale, readStoredLocale } from "../i18n/config";
import { getLoginUiStrings } from "../i18n/loginUiStrings";
import MarketingMeasurementScripts from "../components/MarketingMeasurementScripts";
import { PARTNER_CONSOLE_PATH } from "./Partner/PartnerConsole/partnerConsoleRoutes";
import { PLATFORM_CONSOLE_PATH } from "./Platform/XprobaseConsole/platformConsoleRoutes";

/**
 * Login público sem campo de empresa: portal do representante (`partner`) ou console master (`platform`).
 */
export default function PortalScopeLoginPage({ variant }) {
  const theme = useTheme();
  const brand = useBrand();
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const location = useLocation();
  const { bearerToken, userData, login } = useAuthContext();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mustUpgradePassword, setMustUpgradePassword] = useState(false);
  const [loginCurrentPassword, setLoginCurrentPassword] = useState("");

  const localeChoice = readStoredLocale() || "pt-BR";
  const L = getLoginUiStrings(localeChoice);

  const isPartner = variant === "partner";
  const accent = isPartner ? theme.palette.secondary : theme.palette.primary;

  const safeFrom = location.state?.from;
  const partnerDest =
    typeof safeFrom === "string" && safeFrom.startsWith("/partner") ? safeFrom : PARTNER_CONSOLE_PATH.overview;
  const platformDest =
    typeof safeFrom === "string" && safeFrom.startsWith("/platform") ? safeFrom : PLATFORM_CONSOLE_PATH.workspaces;

  const validateEmail = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);

  const handleSubmit = useCallback(async () => {
    setError("");
    if (!validateEmail(email)) {
      setError(L.invalidEmail);
      return;
    }
    if (!String(password || "").trim()) {
      setError(L.requiredPassword);
      return;
    }

    try {
      localStorage.removeItem("company");
      localStorage.removeItem("xproWorkspaceDatabase");
    } catch {
      /* ignore */
    }

    setLoading(true);
    try {
      const preferredLocale = String(localeChoice || "pt-BR").trim() || "pt-BR";
      setUiLocale(preferredLocale);
      const response = await loginApi(email, password, {
        skipCompanyHeader: true,
        persistToken: false,
        // Mesmo e-mail pode existir no console e no portal; o backend precisa do escopo da tela.
        scope: isPartner ? "partner" : "platform",
      });
      const u = response.data.user || {};
      const scope = u.scope || "workspace";
      const isMaster = Number(u.master) === 1;
      const token = response.data.token;

      if (isPartner) {
        // Portal do representante: aceita APENAS credenciais de representante (scope=partner).
        if (scope === "partner") {
          setBearerToken(token);
          login(token, u);
          try {
            localStorage.setItem("savedEmail", email);
          } catch {
            /* ignore */
          }
          if (response.data.password_needs_upgrade) {
            setLoginCurrentPassword(password);
            setMustUpgradePassword(true);
            return;
          }
          navigate(partnerDest, { replace: true });
          return;
        }
        setError(t("platformPage.portalLoginWrongAccountPartner"));
        return;
      }

      // Console master: aceita APENAS usuários master do xprobase (scope=platform e master=1).
      if (scope === "platform" && isMaster) {
        setBearerToken(token);
        login(token, u);
        try {
          localStorage.setItem("savedEmail", email);
        } catch {
          /* ignore */
        }
        if (response.data.password_needs_upgrade) {
          setLoginCurrentPassword(password);
          setMustUpgradePassword(true);
          return;
        }
        navigate(platformDest, { replace: true });
        return;
      }
      setError(t("platformPage.portalLoginWrongAccountPlatform"));
    } catch (err) {
      setError(err?.message || L.loginFailed);
    } finally {
      setLoading(false);
    }
  }, [
    email,
    password,
    isPartner,
    localeChoice,
    login,
    L.invalidEmail,
    L.requiredPassword,
    L.loginFailed,
    navigate,
    partnerDest,
    platformDest,
    t,
  ]);

  let earlyReturnElement = null;
  if (bearerToken && userData) {
    if (isPartner && userData.scope === "partner") {
      earlyReturnElement = <Navigate to={partnerDest} replace />;
    } else if (!isPartner && userData.scope === "platform" && Number(userData.master) === 1) {
      earlyReturnElement = <Navigate to={platformDest} replace />;
    }
  } else if (bearerToken && userData == null) {
    earlyReturnElement = (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (mustUpgradePassword) {
    return (
      <LegacyPasswordUpgradeDialog
        open
        currentPassword={loginCurrentPassword}
        onComplete={() => {
          setMustUpgradePassword(false);
          navigate(isPartner ? partnerDest : platformDest, { replace: true });
        }}
      />
    );
  }

  if (earlyReturnElement) return earlyReturnElement;

  const titleKey = isPartner ? "platformPage.portalPartnerLoginTitle" : "platformPage.portalPlatformLoginTitle";
  const subtitleKey = isPartner ? "platformPage.portalPartnerLoginSubtitle" : "platformPage.portalPlatformLoginSubtitle";
  const badgeKey = isPartner ? "platformPage.partnerConsoleBadge" : "platformPage.consoleBadge";

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        py: 4,
        px: 2,
        boxSizing: "border-box",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(155deg, ${alpha(accent.main, 0.28)} 0%, #121212 42%, #0d0d0d 100%)`
            : `linear-gradient(155deg, ${alpha(accent.main, 0.2)} 0%, #f7f7f9 38%, #ececf2 100%)`,
      }}
    >
      <MarketingMeasurementScripts />
      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <img
          src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
          alt={brand.name}
          style={{ height: 56 }}
          loading="lazy"
        />
      </Box>

      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 4 },
          maxWidth: 420,
          width: "100%",
          borderRadius: 3,
          border: "1px solid",
          borderColor: (th) => alpha(accent.main, th.palette.mode === "dark" ? 0.35 : 0.25),
          boxShadow: (th) => `0 12px 40px ${alpha(accent.main, 0.12)}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
          <Chip
            label={t(badgeKey)}
            size="small"
            color={isPartner ? "secondary" : "primary"}
            variant="outlined"
            sx={{ mb: 1.5 }}
          />
          <Avatar sx={{ bgcolor: accent.main, mb: 1.5, width: 44, height: 44 }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography variant="h5" component="h1" align="center" fontWeight={700}>
            {t(titleKey)}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, maxWidth: 360 }}>
            {t(subtitleKey)}
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            <Typography variant="body2">{error}</Typography>
          </Alert>
        ) : null}

        <Box
          component="form"
          width="100%"
          display="flex"
          flexDirection="column"
          gap={1.5}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <TextField
            label={L.email}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            autoComplete="username"
            margin="dense"
          />
          <TextField
            label={L.password}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoComplete="current-password"
            margin="dense"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Link
              component={RouterLink}
              to={isPartner ? "/partner/forgot-password" : "/platform/forgot-password"}
              state={{ prefilledEmail: String(email || "").trim() }}
              variant="body2"
              sx={{ fontSize: "0.8125rem", textDecoration: "none", color: "text.secondary" }}
            >
              {L.forgotPassword}
            </Link>
          </Box>
          <Button
            type="submit"
            variant="contained"
            color={isPartner ? "secondary" : "primary"}
            disabled={loading}
            fullWidth
            sx={{ mt: 1, py: 1.25, fontWeight: 700, textTransform: "none", fontSize: "1rem" }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? L.loggingIn : L.login}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2.5 }}>
          <Link component={RouterLink} to="/login" underline="hover" color="inherit" sx={{ fontWeight: 600 }}>
            {t("platformPage.portalLoginStandardLink")}
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
}
